import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DetailIcon from '@mui/icons-material/Details';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, Tooltip } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import AddIcon from '@mui/icons-material/Add';

function UserIndex() {
  // Session validation
  useSessionValidation();
  
  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { isAuthenticated, companyId, userId, logout, token } = useAuth();

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    fetchtableData();
  }, []);

  const fetchtableData = async () => {
    fetchData('UserApi', `/${companyId}/${companyId}`, token)//, token
      .then((response) => {
        if (response.responseCode == '1111') {
          console.log(response);
          setData(response.response_data);
        }
      })
      .catch((error) => {
        console.error('Error fetching User Details:', error);
      });
  };

  const handleDelete = async () => {
    deleteData('UserApi', `${deleteItemId}`, token)//, token
      .then((response) => {
        fetchtableData(); // Refresh data after deletion
        setDeleteItemId(null); // Reset the item ID
        setConfirmDialogOpen(false); // Close the dialog
      })
      .catch((error) => {
        console.error('Error deleting record:', error);
      });

  };

  const handleActivate = (itemId) => {
    activateData('UserApi', `${itemId}`, token)//, token
      .then((response) => {
        fetchtableData(); // Refresh data after deletion
      })
      .catch((error) => {
        // Handle errors for the activation request
      });
  };

  const openConfirmDialog = (itemId) => {
    setDeleteItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  // Define table columns
  const columns = [
    { field: 'FirstName', header: 'First Name' },
    { field: 'LastName', header: 'Last Name' },
    { field: 'UserName', header: 'User Name' },
    { field: 'ContactNumber', header: 'Contact Number' },
    { field: 'Email', header: 'Email' },
  ];

  // Define action buttons for each row
  const renderActions = (item) => (
    <>
      <Link to={`/userDetail/${item.Id}`}>
        <IconButton size="small">
          <DetailIcon style={{ color: 'darksalmon', fontSize: '19px' }} />
        </IconButton>
      </Link>
      <Link to={`/userEdit/${item.Id}`}>
        <IconButton size="small" color="primary">
          <EditIcon style={{ fontSize: '19px' }} />
        </IconButton>
      </Link>
      <IconButton 
        size="small"
        onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))} 
        color="error"
      >
        {item.IsActive ? (
          <DeleteIcon style={{ fontSize: '19px' }} />
        ) : (
          <ActivateIcon style={{ color: 'darkolivegreen', height: '22px' }} />
        )}
      </IconButton>
    </>
  );

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            transition: all 0.3s ease;
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
          .action-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
          }
          .table-row {
            transition: all 0.2s ease;
          }
          .table-row:hover {
            background: #f8f9fa !important;
            transform: scale(1.01);
          }
        `}</style>

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative'
        }}>
          {/* Header Section */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '8px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 8px rgba(102, 126, 234, 0.3)'
              }}>
                <PeopleIcon style={{ fontSize: '22px', color: 'white' }} />
              </div>
              <div>
                <h1 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  margin: 0,
                  letterSpacing: '-0.3px'
                }}>
                  User Management
                </h1>
                <p style={{ margin: 0, color: '#666', fontSize: '11px' }}>
                  View and manage all users
                </p>
              </div>
            </div>
            <Tooltip title="Create a new user" arrow>
              <Button
                variant="contained"
                component={Link}
                to="/userCreate"
                startIcon={<AddIcon />}
                className="action-button"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '6px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                  border: 'none',
                  minHeight: '32px'
                }}
              >
                Create New
              </Button>
            </Tooltip>
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="user table">
                <TableHead>
                  <TableRow style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  }}>
                    {columns.map((column) => (
                      <TableCell key={column.field} style={{
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: '8px 12px'
                      }}>
                        {column.header}
                      </TableCell>
                    ))}
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      padding: '8px 12px'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((item, index) => (
                    <TableRow key={item.Id} className="table-row" style={{
                      background: index % 2 === 0 ? '#ffffff' : '#f8f9fa'
                    }}>
                      {columns.map((column) => (
                        <TableCell key={column.field} style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                          color: '#2d3748'
                        }}>
                          {item[column.field]}
                        </TableCell>
                      ))}
                      <TableCell style={{ padding: '6px 12px' }}>
                        {renderActions(item)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={closeConfirmDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              borderRadius: '12px',
              padding: '8px'
            }
          }}
        >
          <DialogTitle id="alert-dialog-title" style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#2d3748'
          }}>
            Delete Record?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description" style={{
              fontSize: '13px',
              color: '#666'
            }}>
              Are you sure you want to delete this record?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 16px' }}>
            <Button
              onClick={closeConfirmDialog}
              style={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                color: '#ffffff',
                borderRadius: '8px',
                padding: '6px 16px',
                fontSize: '13px',
                fontWeight: '600',
                textTransform: 'none'
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: '#ffffff',
                borderRadius: '8px',
                padding: '6px 16px',
                fontSize: '13px',
                fontWeight: '600',
                textTransform: 'none'
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Footer */}
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 20px',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '16px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          fontFamily: 'serif',
          fontWeight: '600',
          color: '#2c3e50',
          fontSize: '13px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          zIndex: 1000
        }}>
          <CopyrightIcon style={{ fontSize: '18px', color: '#667eea' }} />
          <span>EFFTABS SOLUTIONS LLP</span>
        </div>
      </div>
    </Fade>
  );
}

export default UserIndex;

